import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/**
 * Badge.
 *
 * Six color tones × subtle/solid/outline variants. The subtle
 * variant (default) is the CF "kumo-badge-*-subtle" pattern —
 * tinted bg + matching foreground, no border. That's what you
 * see in CF's dashboard for "Active", "Paused", "✓ ProTier"
 * indicators.
 *
 * Solid is reserved for the loud single-call-out cases (the
 * orange "+ Add" pill on CF, which in our app maps to primary
 * CTAs).
 *
 * Outline is for filter chips where the badge is interactive and
 * needs to communicate "click to toggle me".
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium leading-none transition-colors focus:outline-none focus:ring-1 focus:ring-ring',
  {
    variants: {
      tone: {
        neutral: '',
        orange: '',
        green: '',
        red: '',
        blue: '',
        yellow: '',
      },
      variant: {
        subtle: '',
        solid: '',
        outline: '',
      },
    },
    // Compound variants so we don't end up with 18 nested ternaries
    // at the call site. Each (tone, variant) pair declares exactly
    // its colorway.
    compoundVariants: [
      // --- subtle (default) ---
      { tone: 'neutral', variant: 'subtle', class: 'bg-neutral-800 text-neutral-200' },
      { tone: 'orange', variant: 'subtle', class: 'bg-theia-900/40 text-theia-300' },
      { tone: 'green', variant: 'subtle', class: 'bg-green-950/50 text-green-400' },
      { tone: 'red', variant: 'subtle', class: 'bg-red-950/50 text-red-400' },
      { tone: 'blue', variant: 'subtle', class: 'bg-blue-950/50 text-blue-400' },
      { tone: 'yellow', variant: 'subtle', class: 'bg-yellow-950/40 text-yellow-400' },

      // --- solid ---
      { tone: 'neutral', variant: 'solid', class: 'bg-neutral-200 text-neutral-900' },
      { tone: 'orange', variant: 'solid', class: 'bg-theia-600 text-white' },
      { tone: 'green', variant: 'solid', class: 'bg-green-600 text-white' },
      { tone: 'red', variant: 'solid', class: 'bg-red-600 text-white' },
      { tone: 'blue', variant: 'solid', class: 'bg-blue-600 text-white' },
      { tone: 'yellow', variant: 'solid', class: 'bg-yellow-500 text-yellow-950' },

      // --- outline ---
      { tone: 'neutral', variant: 'outline', class: 'border border-neutral-700 text-foreground' },
      { tone: 'orange', variant: 'outline', class: 'border border-theia-700 text-theia-400' },
      { tone: 'green', variant: 'outline', class: 'border border-green-800 text-green-400' },
      { tone: 'red', variant: 'outline', class: 'border border-red-800 text-red-400' },
      { tone: 'blue', variant: 'outline', class: 'border border-blue-800 text-blue-400' },
      { tone: 'yellow', variant: 'outline', class: 'border border-yellow-800 text-yellow-400' },
    ],
    defaultVariants: {
      tone: 'neutral',
      variant: 'subtle',
    },
  },
);

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, tone, variant, ...props }, ref) => (
    <span
      ref={ref}
      className={cn(badgeVariants({ tone, variant }), className)}
      {...props}
    />
  ),
);
Badge.displayName = 'Badge';

export { badgeVariants };
